<div class="picker" data-testid="design-context-picker" [class.picker--compact]="compact">
  <div class="picker-map" [class.picker-map-tabbed]="canUseDrawings">
    @if (canUseDrawings) {
      <!-- Spec §12 slice 3: a Drawing tab beside the map. The map stays alive under the sheet (its view is costly to rebuild). -->
      <div class="picker-tabs" role="tablist" aria-label="Pick on">
        <button type="button" role="tab" class="picker-tab" data-testid="design-context-picker-tab-map" [class.active]="tab === 'map'"
          [attr.aria-selected]="tab === 'map'" (click)="tab = 'map'">Map</button>
        <button type="button" role="tab" class="picker-tab" data-testid="design-context-picker-tab-drawing" [class.active]="tab === 'drawing'"
          [attr.aria-selected]="tab === 'drawing'" (click)="tab = 'drawing'">Drawing</button>
      </div>
    }
    <div class="picker-map-body">
      <app-esri-map [center]="center" [zoom]="18" (viewLoaded)="onViewLoaded($event)"></app-esri-map>
      <div class="picker-hint text-type-14">Click or drag to place the pin. Click a design feature to link it.</div>
      <!-- The project map's 44px layers / zoom stack and its popup (Project layers + Basemap), contained in this box. -->
      @if (tab === 'map') {
        <app-map-controls
          [contained]="true"
          [organizationBasemaps]="organizationBasemaps"
          [basemapId]="basemapId"
          [projectLayers]="layersToolbar"
          (projectLayerToggle)="onLayerToggle($event)"
          (projectLayerOpacity)="onLayerOpacity($event)"
          (projectLayerLabels)="onLayerLabels($event)"
          (projectLayerZoom)="zoomToLayer($event)"
          (projectLayersReset)="onLayersReset()"
          (basemapChanged)="onBasemapChanged($event)"
          (zoomIn)="zoomBy(1)"
          (zoomOut)="zoomBy(-1)"
        />
      }
      @if (tab === 'drawing') {
        <app-design-context-sheet-picker class="picker-sheet" data-testid="design-context-picker-sheet" [projectId]="data.projectId"
          [station]="data.station ?? null" [link]="link" [canMarkUp]="!capturing" [markupOverlay]="sheetMarkupOverlay" [markupOverlayRevision]="link?.sheet?.revision_uuid ?? null" [hasMarkup]="hasSheetMarkup" (picked)="onSheetPicked($event)" (markUp)="markUpSheet($event)" (removePin)="removeSheetPin()" />
      }
    </div>
  </div>

  <aside class="picker-panel" [attr.aria-label]="tab === 'drawing' ? 'Linked sheet' : 'Design features at the pin'">
    <!-- Title row as on Station range (the other map-in-a-dialog): 28/40/700 and a 24px close. disableClose blocks Esc. -->
    <div class="picker-heading">
      <h2 class="text-type-2 picker-heading-title" data-testid="design-context-picker-title">Location</h2>
      <button type="button" class="picker-close" data-testid="design-context-picker-close" aria-label="Close" (click)="cancel()">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </div>
    @if (data.featureLauncher?.title) { <p class="text-type-14 picker-secondary" data-testid="design-context-picker-launcher-title">{{ data.featureLauncher.title }}</p> }
    @if (layersMessage && tab === 'map') { <p class="text-type-14 picker-secondary" role="status" data-testid="design-context-picker-layers-message">{{ layersMessage }}</p> }
    @if (noMatchingFeatures && !layersMessage && message !== whereEmptyMessage && tab === 'map') { <p class="text-type-14 picker-secondary" role="status" data-testid="design-context-picker-where-empty">{{ whereEmptyMessage }}</p> }

    @if (link) {
      <!-- Parity with mobile 90912be2 (strip-pattern research, 60pt tile): no box; the "…" on the label row, as on
           LOCATION / STATION; the tile marks up, or edits the markup once there is a capture. -->
      <section class="picker-linked" data-testid="design-context-picker-linked" aria-label="Linked feature">
        <div class="picker-strip-header">
          <div class="text-type-13 picker-section-label">{{ isSheetLink ? 'Linked sheet' : 'Linked feature' }}</div>
          <button type="button" class="picker-menu-trigger" data-testid="design-context-picker-menu" aria-label="Linked feature actions"
            [matMenuTriggerFor]="pickerLinkMenu">
            <mat-icon aria-hidden="true">more_horiz</mat-icon>
          </button>
          <mat-menu #pickerLinkMenu="matMenu" xPosition="before">
            @if (isSheetStrip) {
              <button mat-menu-item type="button" data-testid="design-context-picker-menu-sheet-markup" [disabled]="!canMarkUp" (click)="markUpSheet()">
                <mat-icon aria-hidden="true">edit</mat-icon>
                {{ heldSheetPreview ? 'Edit markup' : 'Mark up' }}
              </button>
            } @else if (hasCaptureImage) {
              <button mat-menu-item type="button" data-testid="design-context-picker-menu-edit-markup" [disabled]="capturing" (click)="editMarkup()">
                <mat-icon aria-hidden="true">edit</mat-icon>
                Edit markup
              </button>
            } @else if (canMarkUp) {
              <button mat-menu-item type="button" data-testid="design-context-picker-menu-markup" (click)="markUp()">
                <mat-icon aria-hidden="true">edit</mat-icon>
                Mark up
              </button>
            }
            @if (capture && !isSheetStrip) {
              <button mat-menu-item type="button" data-testid="design-context-picker-delete-markup" [disabled]="capturing" (click)="confirmDeleteMarkup()">
                <mat-icon aria-hidden="true">layers_clear</mat-icon>
                Delete markup
              </button>
            }
            <button mat-menu-item type="button" data-testid="design-context-picker-remove" [disabled]="capturing" (click)="removeLink()">
              <mat-icon svgIcon="trash" aria-hidden="true" />
              Remove link
            </button>
          </mat-menu>
        </div>
        <div class="picker-strip-info" data-testid="design-context-picker-strip-info">
          @if (isSheetStrip) {
            <!-- The sheet point: the tile marks up the sheet page; strokes are held until the record saves. -->
            <!-- W75: the crop of the sheet around the pin (as mobile's SHEET card), or the page with the held strokes; on hover a
                 pencil badge and the white tooltip say what a click does -->
            <button type="button" class="picker-strip-thumb picker-strip-markup picker-strip-tile" data-testid="design-context-picker-sheet-markup"
              [class.picker-strip-sheet]="!!sheetTilePage || !!sheetTileSrc"
              [attr.aria-label]="sheetTileLabel" [matTooltip]="sheetTileLabel" [disabled]="!canMarkUp" (click)="tapImage()">
              @if (sheetTilePage; as page) {
                <!-- W51: the page with the strokes held for the record, not yet saved -->
                <img class="picker-strip-sheet-page" data-testid="design-context-picker-sheet-thumb" [src]="page" alt="" />
                <img class="picker-strip-sheet-page" data-testid="design-context-picker-sheet-thumb-markup" [src]="sheetMarkupOverlay" alt="" />
              } @else if (sheetTileSrc; as src) {
                @if (sheetTileCrop; as crop) {
                  <span class="picker-tile-page" [style.width.%]="crop.width * 100" [style.left.%]="crop.left * 100" [style.top.%]="crop.top * 100">
                    <img class="picker-tile-img" data-testid="design-context-picker-sheet-crop" [src]="src" alt="" draggable="false" />
                  </span>
                  @if (link.sheet?.point) {
                    <img class="picker-tile-pin" data-testid="design-context-picker-sheet-crop-pin" src="/assets/icons/map/markers/location-pin-filled.svg" alt=""
                      [style.left.%]="crop.pinLeft * 100" [style.top.%]="crop.pinTop * 100" />
                  }
                } @else {
                  <!-- Measures the page's aspect; the crop appears once it has loaded -->
                  <img class="picker-tile-measure" [src]="src" alt="" (load)="onSheetTileLoad($event)" />
                }
              } @else {
                <mat-icon aria-hidden="true">edit</mat-icon>
              }
              @if (sheetTilePage || sheetTileSrc) {
                <span class="picker-tile-badge" data-testid="design-context-picker-sheet-badge" aria-hidden="true"><mat-icon>edit</mat-icon></span>
              }
            </button>
          } @else if (hasCaptureImage) {
            <!-- The shared preview as a button (cover); the strip lists the hint itself (Map moved follows the live pin). -->
            <app-design-capture-preview class="picker-strip-thumb" data-testid="design-context-picker-edit-markup" [link]="link" height="60px"
              actionLabel="Edit markup" [showBadges]="false" [disabled]="capturing" (activate)="tapImage()" />
          } @else {
            <button type="button" class="picker-strip-thumb picker-strip-markup" data-testid="design-context-picker-markup"
              aria-label="Mark up the map" [attr.aria-busy]="capturing" [disabled]="!canMarkUp" (click)="tapImage()">
              @if (capturing) {
                <mat-spinner diameter="24" aria-hidden="true" />
              } @else {
                <mat-icon aria-hidden="true">edit</mat-icon>
              }
            </button>
          }
          <div class="picker-strip-text">
            <div class="mat-subtitle-1 picker-strip-title" data-testid="design-context-picker-strip-title" [attr.title]="stripLines.title">{{ stripLines.title }}</div>
            @if (stripLines.detail) {
              <div class="text-type-14 picker-secondary picker-strip-line" data-testid="design-context-picker-strip-detail" [attr.title]="stripLines.detail">{{ stripLines.detail }}</div>
            }
            @if (isSheetLink && heldSheetPreview) {
              <!-- Spec §12 slice 3 copy: "… · Marked up". The strokes are posted once the record saves. -->
              <div class="text-type-14 picker-secondary picker-strip-line" data-testid="design-context-picker-sheet-marked">Marked up · saved with the {{ recordLabel }}</div>
            }
            @if (captureError) {
              <div class="text-type-14 picker-error picker-strip-line" role="alert" data-testid="design-context-picker-capture-error" [attr.title]="captureError">{{ captureError }}</div>
            } @else if (stripHint) {
              <div class="text-type-14 picker-secondary picker-strip-line" data-testid="design-context-picker-capture-moved">{{ stripHint }}</div>
            }
          </div>
        </div>
      </section>
    } @else if (tab === 'drawing') {
      <p class="text-type-14 picker-secondary" data-testid="design-context-picker-sheet-hint">Choose a sheet, then click the spot or mark it up to link this {{ recordLabel }}.</p>
    } @else {
      <div class="picker-panel-header">
        @if (view === 'details' && candidates.length > 1) {
          <button type="button" class="picker-back" aria-label="Back to the list" (click)="backToList()"><mat-icon svgIcon="chevron-left"></mat-icon></button>
        }
        <h3 class="mat-subtitle-1 picker-title">{{ loading ? 'Looking up…' : (view === 'details' ? selected?.label : 'Features here') }}</h3>
      </div>
      @if (message) { <p class="text-type-14 picker-secondary" role="status" data-testid="design-context-picker-message">{{ message }}</p> }
      @if (incomplete) { <p class="text-type-14 picker-secondary" role="status">Some features could not be checked. Zoom in for a complete result.</p> }
      @if (view === 'list' && candidates.length > 1) {
        <ul class="picker-list" aria-label="Nearby design features">
          @for (candidate of candidates; track candidate.layer.id + ':' + candidate.featureId + ':' + (candidate.variant || '')) {
            <li>
              <button type="button" class="picker-row" data-testid="design-context-picker-row" (click)="selectCandidate(candidate)"
                (mouseenter)="hoverCandidate(candidate)" (mouseleave)="hoverCandidate(null)" (focus)="hoverCandidate(candidate)" (blur)="hoverCandidate(null)">
                <span class="picker-row-top">
                  <span class="text-type-14 picker-row-label">{{ candidate.label }}</span>
                  @if (data.featureLauncher && linkSummary) {
                    <!-- Status icons: Marker spec v3 (helpers/status-marker.ts), 16 px in lists. -->
                    @if (rowEntry(candidate)) {
                      <span class="text-type-14 picker-row-status picker-row-status--done" data-testid="design-context-picker-row-done"><svg class="picker-status-icon" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="#36AB19"/><path transform="translate(3 3)" d="M2.91667 8.01596L6.09073 11.0833L11.6667 2.91667" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>Done</span>
                    } @else {
                      <span class="text-type-14 picker-row-status picker-row-status--pending" data-testid="design-context-picker-row-pending"><svg class="picker-status-icon" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="#E02020" stroke="#FFFFFF" stroke-width="2"/></svg>Pending</span>
                    }
                  }
                </span>
                @if (candidate.meta) { <span class="text-type-13 picker-secondary picker-row-sub">{{ candidate.meta }}</span> }
                @if (data.featureLauncher && rowEntry(candidate)) {
                  <span class="text-type-13 picker-secondary picker-row-sub" data-testid="design-context-picker-row-done-caption">{{ rowDoneCaption(candidate) }}</span>
                }
              </button>
            </li>
          }
        </ul>
        @if (hidden) { <p class="text-type-14 picker-secondary">{{ hidden }} more nearby. Zoom in to narrow it down.</p> }
      }
      @if (view === 'details' && selected; as candidate) {
        <div class="picker-details" data-testid="design-context-picker-details">
          <h4 class="text-type-13 picker-section-label picker-layer-name">{{ candidate.layer.name }}</h4>
          <dl class="picker-fields text-type-14">
            @for (field of candidate.fields; track $index) { <div><dt>{{ field.label }}</dt><dd>{{ field.value }}</dd></div> }
          </dl>
          @if (!candidate.fields.length) { <p class="text-type-14 picker-secondary">No details recorded for this feature.</p> }
          @if (data.featureLauncher && rowEntry(candidate)) {
            <!-- A done row (Addendum 3): open the newest entry, or add another test of the same form. -->
            <p class="text-type-14 picker-row-status--done picker-done-line" data-testid="design-context-picker-done-line">
              <svg class="picker-status-icon" width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="#36AB19"/><path transform="translate(3 3)" d="M2.91667 8.01596L6.09073 11.0833L11.6667 2.91667" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>Done <span class="picker-secondary">· {{ rowDoneCaption(candidate) }}</span>
            </p>
            <div class="picker-done-actions">
              <button type="button" class="picker-link mat-subtitle-1" data-testid="design-context-picker-open-existing"
                [disabled]="!canOpenExisting" (click)="openExistingEntry()">Open existing entry</button>
              @if (!canOpenExisting) { <span class="text-type-14 picker-secondary" data-testid="design-context-picker-existing-note">{{ existingEntryNote }}</span> }
              <button type="button" class="picker-link mat-subtitle-1" data-testid="design-context-picker-add-another" (click)="linkSelected()">Add another entry</button>
              <button type="button" class="picker-link picker-link--muted mat-subtitle-1" data-testid="design-context-picker-done-cancel"
                (click)="candidates.length > 1 ? backToList() : cancel()">Cancel</button>
            </div>
          } @else {
            <button type="button" class="picker-link mat-subtitle-1" data-testid="design-context-picker-link" (click)="linkSelected()">{{ linkActionLabel }}</button>
          }
        </div>
      }
    }
  </aside>

  <div class="picker-footer">
    @if (data.enableLocationSection) {
      <app-text-area [parentFormGroup]="locationForm" controlName="location" label="Location" placeholder="Enter location" />
    }
    @if (data.showIncludeGpsLocationCheckbox) {
      <mat-checkbox [(ngModel)]="includeGps" color="primary">Include GPS location</mat-checkbox>
    }
    <div class="picker-buttons">
      <app-secondary-button class="picker-button" (handleClick)="cancel()">Cancel</app-secondary-button>
      <app-primary-button class="picker-button" (handleClick)="done()" [isDisabled]="isDoneDisabled || capturing" data-testid="design-context-picker-done">{{ saveLabel }}</app-primary-button>
    </div>
  </div>
</div>

@if (showMarkupEditor && markupConfig) {
  <!-- Same guard as app-design-context: the editor's colour/size inputs fire native change/input events. -->
  <div class="picker-markup-editor" (change)="$event.stopPropagation()" (input)="$event.stopPropagation()">
    <app-markup-editor [config]="markupConfig" (saved)="onEditorSaved($event)" (closed)="closeMarkupEditor()"></app-markup-editor>
  </div>
}
